/**
 * The failure chat's API client throws (ADR-20260925, applied to chat by
 * PLAN-20260929 slice 9): what happened, as typed fields, on the shared
 * @agion/ui/api-failure contract admin-ui uses too. Its message is a
 * diagnostic for logs and for the current chat, which renders it today; the
 * new chat's screens choose their words from these fields instead.
 */

import { ApiFailure, type ApiFailureKind } from '@agion/ui/api-failure'

export type { ApiFailureKind }

/**
 * Chat's adapter failure: the shared ApiFailure (kind, status, code, data,
 * retryable) plus the `response` shape the current chat already reads
 * (agion/Agion#2413).
 */
export class ChatApiError extends ApiFailure {
  readonly response?: { status: number; data?: unknown }

  constructor(message: string, init: { kind: ApiFailureKind; status?: number; data?: unknown }) {
    super(message, init)
    this.name = 'ChatApiError'
    if (init.kind === 'http' || init.kind === 'unauthorized') {
      this.response = init.kind === 'http' ? { status: this.status, data: init.data } : { status: this.status }
    }
  }
}

export function isChatApiError(error: unknown): error is ChatApiError {
  return error instanceof ChatApiError
}

/**
 * The Edge host a refusal names as offline (edge_host_offline,
 * agion/Agion#2485): the conversation's history or files are on it. The name
 * is present when the server could read it.
 */
export function offlineEdgeHost(error: unknown): { hostName?: string } | undefined {
  if (!isChatApiError(error) || error.code !== 'edge_host_offline') return undefined
  const data = (error.data ?? {}) as Record<string, unknown>
  const name = data['host_name']
  return { hostName: typeof name === 'string' && name !== '' ? name : undefined }
}

/**
 * Whether the server refused a run's content to this reader (403
 * conversation_not_viewable, agion/Agion#3019): the run exists, its
 * conversation belongs to another person.
 */
export function isConversationNotViewable(error: unknown): boolean {
  return isChatApiError(error) && error.code === CONVERSATION_NOT_VIEWABLE
}

/** The refusal code of a run's content, on HTTP and on the live stream. */
export const CONVERSATION_NOT_VIEWABLE = 'conversation_not_viewable'

/** Whether the failure is an Edge host that is not recorded or not a host. */
export function isUnknownEdgeHost(error: unknown): boolean {
  return error instanceof EdgeHostUnknownError || (isChatApiError(error) && error.code === 'edge_host_unknown')
}

/**
 * fetch for callers that cannot use ApiClient (a PATCH straight to
 * auth-service's user routes): the same typed failure on no response or a
 * refused one. The body is kept as JSON, or as its text when it is plain text
 * (Go http.Error); an HTML error page from a proxy is dropped.
 */
export async function fetchOrFail(url: string, init: RequestInit): Promise<Response> {
  let response: Response
  try {
    response = await fetch(url, init)
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ChatApiError('Request cancelled', { kind: 'cancelled' })
    }
    throw new ChatApiError(error instanceof Error ? error.message : 'Network error', { kind: 'network' })
  }
  if (response.ok) return response
  const raw = (await response.text().catch(() => '')).trim()
  let data: unknown = undefined
  if (raw !== '' && !raw.startsWith('<')) {
    try {
      data = JSON.parse(raw)
    } catch {
      data = raw
    }
  }
  // A 401 here can be the member's own refusal (a wrong current password on
  // auth-service's password route), so it stays an http failure with its body.
  throw new ChatApiError(`HTTP ${response.status}`, { kind: 'http', status: response.status, data })
}

/**
 * A past run whose conversation lives on an Edge host the platform did not
 * record: the mission is bound to a host group and neither it nor the run
 * names the host (agion/Agion#2481). Retrying cannot find it.
 */
export class EdgeHostUnknownError extends Error {
  constructor() {
    super('The Edge host that ran this mission is not recorded')
    this.name = 'EdgeHostUnknownError'
  }
}
